import { useFabricObject } from "@/hooks/use-fabric-object";
import { useCursorRenderer } from "@/hooks/use-cursor-renderer";
import { useWhiteboardMediaState } from "@/hooks/use-whiteboard-media-state";
import { TeacherClassLocale } from "@/locales/localeid";
import { AnnotationMode, MediaType, MousePositionEventData } from "@/models";
import { ExposureItem, ExposureItemMedia } from "@/store/lesson/state";
import {
  ALTERNATE_AUDIO_ID,
  ALTERNATE_VIDEO_ID,
  Cursor,
  DEFAULT_ALTERNATE_CONTENT_VOLUME_FOR_STUDENT,
  DefaultCanvasDimension,
  isRotatedLPSlide,
} from "@/utils/utils";
import { Alert, Popover } from "ant-design-vue";
import { fabric } from "fabric";
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect } from "vue";
import { fmtMsg } from "vue-glcommonui";
import VuePdfEmbed from "vue-pdf-embed";
import { useStore } from "vuex";
import { useElementSize, watchDebounced } from "@vueuse/core";
import { useFocusWord } from "@/hooks/use-focus-word";
import { useWhiteboard } from "@/hooks/use-whiteboard";
import { initCanvas } from "@/hooks/use-canvas";
import { useCanvasObjectUpdate } from "@/hooks/use-canvas-object-update";
import { useCanvasFullSync } from "@/hooks/use-canvas-full-sync";
import { useImageLoader } from "@/hooks/use-image-loader";
import { useTarget } from "@/hooks/use-target";
import * as slideUtils from "@/utils/slide-utils";
import { vuexName, MODULES } from "@/store/utils";
import { StudentState } from "@/store/room/interface";
import { useAnnotationStore } from "@/hooks/use-annotation-store";
import { REALTIME_CHANNEL, resolveChannelName } from "@/ws";
import { useRealtimeSpace } from "@/hooks/use-realtime-space";
import { StudentRoomManager } from "@/manager/room/student.manager";
import { useRealtimeDrawing } from "@/hooks/use-realtime-drawing";
import {
  applyLessonBackground,
  applyWhiteboardBackground,
  clearAllCanvasObjects,
  clearCanvasExceptBackgroundAndTargets,
  ConnectObjectTypes,
  DEFAULT_LESSON_IMAGE_URL,
  FbObjectOrigins,
  FbObjects,
  isLessonBackgroundObject,
  setCanvasDrawingMode,
  setGroupVisibility,
  setTargetsVisibility,
} from "@/utils/fabric-utils";
import { Colors } from "@/utils/constant";
import { XYCoords } from "@/store/annotation-v2/state";
const COMPONENT_NAME = "AnnotationView";

export default defineComponent({
  name: COMPONENT_NAME,
  props: ["image"],
  components: {
    Popover,
    VuePdfEmbed,
    Alert,
  },
  setup(props) {
    // ---------------------------------------------------------------------------
    // Canvas & core refs
    // ---------------------------------------------------------------------------
    let canvas: any;
    let group: any;
    let point: any;

    const canvasRef = ref(null);
    const containerRef = ref<HTMLDivElement>();
    const pdfRef = ref<InstanceType<typeof VuePdfEmbed>>();

    // ---------------------------------------------------------------------------
    // Routing, store, realtime
    // ---------------------------------------------------------------------------
    const { getters, dispatch, commit } = useStore();

    const realtimeChannels = computed(() => getters[vuexName(MODULES.REALTIME_SETTINGS.GETTERS.GET_SETTINGS)]);
    const roomManager = computed<StudentRoomManager | undefined>(() => getters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_ROOM_MANAGER)]);
    const getRealtimeClient = () => roomManager.value?.WSClient?.getRealtimeTransport()?.getRealtimeClient();

    // ---------------------------------------------------------------------------
    // Hooks: media, annotation, whiteboard, object state
    // ---------------------------------------------------------------------------
    const { videoRef, audioRef, toggleMediaCanPlay } = useWhiteboardMediaState();

    useCanvasFullSync();
    const { resetReloadKey, reloadKey, handleImageLoadError } = useImageLoader(false);
    const { initializeTargets } = useTarget();
    const { isWbVisible } = useWhiteboard();
    const { naturalWidthOfCurrentSlide, naturalHeightOfCurrentSlide } = useAnnotationStore();
    const { focusWordContent } = useFocusWord();
    // ---------------------------------------------------------------------------
    // DOM / media refs
    // ---------------------------------------------------------------------------
    const imgCropRef = ref<any>(null);
    const imgRef = ref<HTMLElement | null>(null);
    const videoAnnotation = ref<HTMLVideoElement | null>(null);
    const audioAnnotation = ref<HTMLVideoElement | null>(null);

    // Audio background timing
    const audioLocalTime = ref(0);
    const resetAudioBackgroundState = () => {
      audioLocalTime.value = 0;
    };

    // ---------------------------------------------------------------------------
    // Basic computed from store
    // ---------------------------------------------------------------------------
    const currentExposure = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE)]);
    const currentExposureItemMedia = computed<ExposureItemMedia | undefined>(
      () => getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)],
    );
    const isPointerMode = computed(() => getters[vuexName(MODULES.ANNOTATION_V2.GETTERS.IS_POINTER_MODE)]);
    const WarningAudioTagText = computed(() => fmtMsg(TeacherClassLocale.WarningAudioTag));
    const WarningVideoTagText = computed(() => fmtMsg(TeacherClassLocale.WarningVideoTag));

    const { width: containerWidth } = useElementSize(containerRef);

    const student = computed<StudentState | undefined>(() => getters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_STUDENT)]);
    const oneToOneStudentId = computed(() => getters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_ONE_TO_ONE_STUDENT_ID)]);

    const isMeOneOneWithHelper = computed(() => getters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_IN_ONE_TO_ONE_MODE_WITH_HELPER)]);
    const isMeOneOneWithTeacher = computed(() => getters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_IN_ONE_TO_ONE_MODE_WITH_TEACHER)]);

    const zoomRatio = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_ZOOM_RATIO)]);
    const imgCoords = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_IMG_COORDS)]);

    const alternateMediaState = computed(() => getters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_MEDIA_STATE)]);
    const isTeacherDisconnected = computed<boolean>(() => getters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_TEACHER_IS_DISCONNECTED)]);

    const isPaletteVisible = computed(
      () => (student.value?.isPalette && !oneToOneStudentId.value) || (student.value?.isPalette && isMeOneOneWithTeacher.value),
    );

    const isTeacherUseOnly = computed<boolean>(() => !!currentExposureItemMedia.value?.teacherUseOnly && !isWbVisible.value);

    const isValidUrl = computed(() => props.image?.url && props.image?.url !== DEFAULT_LESSON_IMAGE_URL);

    const isCropImage = computed(() => !!props.image?.metaData && props.image.metaData.width > 0 && props.image.metaData.height > 0);

    // Use composable for realtime drawing (Student only)
    const { handleDrawingData, clearAllPaths, removePathsByObjectId } = useRealtimeDrawing({
      defaultStrokeColor: Colors.Black,
    });

    useCanvasObjectUpdate({
      onClearAll: () => clearAllPaths(),
      onRemoveObject: (objectId) => removePathsByObjectId(objectId),
    });

    // ---------------------------------------------------------------------------
    // Live cursor (teacher → student)
    // ---------------------------------------------------------------------------
    const pointerCoords = ref<XYCoords | null>(null);

    const resolveCursorChannelName = () => {
      if (isMeOneOneWithHelper.value) {
        return resolveChannelName(REALTIME_CHANNEL.HELPER_ONE_ONE, realtimeChannels.value);
      }

      if (isMeOneOneWithTeacher.value) {
        return resolveChannelName(REALTIME_CHANNEL.TEACHER_ONE_ONE, realtimeChannels.value);
      }

      return resolveChannelName(REALTIME_CHANNEL.MAIN, realtimeChannels.value);
    };

    useRealtimeSpace({
      getRealtime: getRealtimeClient,
      resolveChannel: resolveCursorChannelName,
      onCursor: (payload: MousePositionEventData) => {
        if (!payload || isTeacherUseOnly.value) return;
        // Check for special value to hide cursor (when teacher mouse leaves canvas)
        if (!isPointerMode.value) {
          commit(vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_ANNOTATION_MODE), AnnotationMode.PointerMode);
        }
        if (payload.x === -1 && payload.y === -1) {
          pointerCoords.value = null;
          return;
        }
        pointerCoords.value = { x: payload.x, y: payload.y };
      },
      onDrawing: (payload) => {
        // Student receives drawing data from Teacher
        if (!payload?.drawingData || isTeacherUseOnly.value || !canvas) return;
        handleDrawingData(payload);
      },
    });

    const { pointerStyle, scaleRatio } = useCursorRenderer(containerRef, pointerCoords);
    // ---------------------------------------------------------------------------
    // Canvas transform helpers
    // ---------------------------------------------------------------------------
    const transformCanvas = () => {
      canvas?.setViewportTransform([
        scaleRatio.value * zoomRatio.value,
        0,
        0,
        scaleRatio.value * zoomRatio.value,
        (imgCoords.value?.x ?? 0) * scaleRatio.value,
        (imgCoords.value?.y ?? 0) * scaleRatio.value,
      ]);
    };

    const resizeCanvas = () => {
      const outerCanvasContainer = containerRef.value;
      if (!outerCanvasContainer || !outerCanvasContainer.clientWidth) return;

      const { width, height } = DefaultCanvasDimension;
      const ratio = width / height;
      const containerWidthValue = outerCanvasContainer.clientWidth;

      scaleRatio.value = containerWidthValue / width;

      canvas.setDimensions({
        width: containerWidthValue,
        height: containerWidthValue / ratio,
      });

      transformCanvas();
      point = new fabric.Point(containerWidthValue / 2, containerWidthValue / ratio / 2);
    };

    // ---------------------------------------------------------------------------
    // Fabric object helpers
    // ---------------------------------------------------------------------------
    const { onObjectCreated, FontLoader, onObjectRemoved } = useFabricObject();

    const listenToCanvasEvents = () => {
      onObjectCreated(canvas);
      onObjectRemoved(canvas);
    };

    // ---------------------------------------------------------------------------
    // Media type & alternate media URL
    // ---------------------------------------------------------------------------
    const mediaTypeId = computed(() => {
      const nextId = getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)]?.id;
      if (!nextId) return;

      let result;
      const listMedia = currentExposure.value?.alternateMediaBlockItems.flat();
      if (listMedia) {
        const target = listMedia.find(({ id }: ExposureItem) => nextId === id);
        if (target) {
          result = target.mediaTypeId;
        }
      }
      return result;
    });

    watch(
      [mediaTypeId, currentExposureItemMedia],
      async ([newMediaTypeId, newExposureItem]) => {
        if (typeof newMediaTypeId === "number" && newExposureItem?.image?.url === DEFAULT_LESSON_IMAGE_URL) {
          await dispatch(vuexName(MODULES.LESSON.DISPATCHES.GET_ALTERNATE_MEDIA_URL), {
            id: newExposureItem.id,
            mediaBlobUrl: newExposureItem.image.mediaBlobUrl,
            mediaTypeId: newMediaTypeId as MediaType,
          });
        }
      },
      { immediate: true },
    );

    // ---------------------------------------------------------------------------
    // Whiteboard mode handling
    // ---------------------------------------------------------------------------
    const updateCanvasForWhiteboardMode = () => {
      if (!canvas) return;
      clearCanvasExceptBackgroundAndTargets(canvas);
      clearAllPaths();
      if (isWbVisible.value) {
        applyWhiteboardBackground(canvas);
        setTargetsVisibility(canvas, false);
        setGroupVisibility(group, false);
      } else {
        applyLessonBackground(canvas);
        setCanvasDrawingMode(canvas, false);
        setGroupVisibility(group, true);
        setTargetsVisibility(canvas, true);
      }
      canvas.renderAll();
    };

    watch(isWbVisible, updateCanvasForWhiteboardMode);

    // ---------------------------------------------------------------------------
    // Slide initialization on canvas
    // ---------------------------------------------------------------------------
    const initializeSlideOnCanvas = ({
      propImage,
      canvas,
      imgEl,
      isShowWhiteBoard,
    }: {
      propImage: any;
      canvas: any;
      imgEl: any;
      isShowWhiteBoard: boolean;
    }) => {
      if (!propImage?.url) {
        return;
      }

      const isRotatedSlideFlag = isRotatedLPSlide(propImage?.metaData);
      const imgWidth = isRotatedSlideFlag ? naturalHeightOfCurrentSlide.value : naturalWidthOfCurrentSlide.value;
      const imgHeight = isRotatedSlideFlag ? naturalWidthOfCurrentSlide.value : naturalHeightOfCurrentSlide.value;

      const canvasGroup = canvas.getObjects().find((item: any) => isLessonBackgroundObject(item));
      const imageRatio = Math.max(imgWidth / DefaultCanvasDimension.width, imgHeight / DefaultCanvasDimension.height);
      const renderWidth = imgWidth / imageRatio;
      const renderHeight = imgHeight / imageRatio;

      if (canvasGroup) {
        canvas.remove(canvasGroup);
      }

      const clipPath = new fabric.Rect({
        width: renderWidth,
        height: renderHeight,
        left: (DefaultCanvasDimension.width - renderWidth) / 2,
        top: 0,
        absolutePositioned: true,
      });

      const angle = propImage.metaData ? (propImage.metaData.width > 0 && propImage.metaData.height > 0 ? 0 : propImage.metaData.rotate) : 0;

      const Image = new fabric.Image(imgEl, {
        id: ConnectObjectTypes.LessonBackground,
        clipPath,
        originX: FbObjectOrigins.Center,
        originY: FbObjectOrigins.Center,
        angle,
        selectable: false,
        hasBorders: false,
        hasControls: false,
      });

      Image.scaleToWidth(renderWidth);
      Image.scaleToHeight(renderHeight);

      const left = DefaultCanvasDimension.width / 2;
      const top = renderHeight / 2;

      const Group = new fabric.Group([Image], {
        id: ConnectObjectTypes.LessonBackground,
        clipPath,
        originX: FbObjectOrigins.Center,
        originY: FbObjectOrigins.Center,
        left,
        top,
        selectable: isPaletteVisible.value,
        visible: !isShowWhiteBoard,
        hoverCursor: Cursor.Pointer,
        scaleX: propImage?.metaData?.scaleX ?? 1,
        scaleY: propImage?.metaData?.scaleY ?? 1,
        hasBorders: false,
        hasControls: false,
        layout: "clip-path",
        interactive: true,
        subTargetCheck: true,
        lockMovementX: true,
        lockMovementY: true,
      });

      if (top !== Group.top) {
        Group.realTop = Group.top;
      }
      if (left !== Group.left) {
        Group.realLeft = Group.left;
      }

      canvas.add(Group);
      canvas.sendToBack(Group);
      transformCanvas();

      return Group;
    };

    const onLPSlideLoaded = async (e?: Event) => {
      if (e) toggleImageFetchingSpin(false);

      let img: HTMLImageElement | null;
      if (e) {
        img = e.target as HTMLImageElement;
      } else {
        img = isCropImage.value ? imgCropRef.value?.imageRef : (imgRef.value as HTMLImageElement | null);
      }

      if (!img) return;

      commit(vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_IMAGE_DIMENSIONS), {
        width: img.naturalWidth && img.naturalHeight ? img.naturalWidth : undefined,
        height: img.naturalWidth && img.naturalHeight ? img.naturalHeight : undefined,
      });

      img.crossOrigin = "anonymous";

      group = initializeSlideOnCanvas({
        propImage: props.image,
        canvas,
        imgEl: img,
        isShowWhiteBoard: isWbVisible.value,
      });

      if (props.image?.metaData?.annotations) {
        initializeTargets(props.image.metaData);
      }
    };

    // ---------------------------------------------------------------------------
    // Init whiteboard (canvas)
    // ---------------------------------------------------------------------------
    const initWhiteboard = async () => {
      if (!canvasRef.value) return;

      const { width, height } = DefaultCanvasDimension;
      canvas = new fabric.Canvas(canvasRef.value, {
        width,
        height,
      });

      initCanvas(canvas);
      canvas.preserveObjectStacking = true;
      canvas.selectionFullyContained = false;

      canvas.getObjects(FbObjects.Path).forEach((obj: any) => {
        obj.selectable = false;
        obj.evented = false;
        obj.perPixelTargetFind = true;
      });

      await FontLoader.load();
      listenToCanvasEvents();
      resizeCanvas();
      updateCanvasForWhiteboardMode();
    };

    // ---------------------------------------------------------------------------
    // Zoom & coords sync
    // ---------------------------------------------------------------------------
    watch(
      zoomRatio,
      () => {
        transformCanvas();
      },
      { immediate: true },
    );

    watch(imgCoords, (newValue) => {
      if (newValue) {
        transformCanvas();
      }
      canvas?.renderAll();
    });

    // ---------------------------------------------------------------------------
    // Slide / exposure watchers
    // ---------------------------------------------------------------------------
    watch(currentExposureItemMedia, async (newValue, oldValue) => {
      if (
        slideUtils.isTeacherNotChooseAnySlide(newValue) ||
        slideUtils.isTeacherChooseTeacherUseOnlySlide(newValue) ||
        slideUtils.isTeacherChangedFromCurrentSlideToOtherSlideOrNoSlide(newValue, oldValue)
      ) {
        clearAllCanvasObjects(canvas);
        clearAllPaths();
      }

      if (newValue) {
        transformCanvas();
      }

      if (newValue && oldValue && newValue.image.url === oldValue.image.url) {
        await onLPSlideLoaded();
      }

      resetAudioBackgroundState();
    });

    watch(
      () => props.image?.url,
      () => {
        videoAnnotation.value?.load();
        audioAnnotation.value?.load();
      },
    );

    // ---------------------------------------------------------------------------
    // One-to-one student handling
    // ---------------------------------------------------------------------------
    watch([isMeOneOneWithTeacher, isMeOneOneWithHelper], ([newTeacher, newHelper], [oldTeacher, oldHelper]) => {
      if ((oldTeacher && !newTeacher) || (oldHelper && !newHelper)) {
        clearAllPaths();
      }
    });

    // ---------------------------------------------------------------------------
    // Media state sync (play/pause)
    // ---------------------------------------------------------------------------
    watch(
      alternateMediaState,
      () => {
        if (videoAnnotation.value) {
          alternateMediaState.value === true ? videoAnnotation.value.play() : videoAnnotation.value.pause();
        }
        if (audioAnnotation.value) {
          alternateMediaState.value === true ? audioAnnotation.value.play() : audioAnnotation.value.pause();
        }
      },
      { deep: true },
    );

    watch(videoAnnotation, (currentValue) => {
      videoRef.value = currentValue;
    });

    watch(audioAnnotation, (currentValue) => {
      audioRef.value = currentValue;
    });

    // ---------------------------------------------------------------------------
    // Container resize & canvas resize
    // ---------------------------------------------------------------------------
    watchDebounced(
      containerWidth,
      (value) => {
        if (value) {
          resizeCanvas();
          if (zoomRatio.value && point) {
            canvas.zoomToPoint(point, zoomRatio.value * scaleRatio.value);
          }
          pdfRef.value?.render();
        }
      },
      { debounce: 300 },
    );

    // ---------------------------------------------------------------------------
    // PDF sync (scroll)
    // ---------------------------------------------------------------------------
    const pdfHeight = ref<number[]>([]);

    const onPdfRendered = () => {
      pdfHeight.value = [];
      document.querySelectorAll(".pdf-config canvas").forEach((element) => {
        pdfHeight.value.push(element.clientHeight);
      });
      if (pdfScrollProgress.value) {
        scrollPdfToPercentage(pdfScrollProgress.value, false);
      }
    };

    const pdfScrollProgress = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_PDF_SCROLL_PROGRESS)]);

    const scrollPdfToPercentage = (percentage: number, isSmooth = true) => {
      const pdfElement = document.querySelector(".pdf-config");
      let currentPage = 0;
      let scrollHeight = 0;

      while (percentage > 0) {
        if (percentage >= 100) {
          scrollHeight += pdfHeight.value[currentPage];
          percentage -= 100;
          currentPage++;
        } else {
          scrollHeight += (pdfHeight.value[currentPage] * percentage) / 100;
          percentage = 0;
          currentPage++;
        }
      }

      pdfElement?.scrollTo({
        top: scrollHeight,
        behavior: isSmooth ? "smooth" : "auto",
      });
    };

    watch(pdfScrollProgress, (progress) => scrollPdfToPercentage(progress));

    // ---------------------------------------------------------------------------
    // Audio background (image timeline)
    // ---------------------------------------------------------------------------
    const audioBackground = computed(() => {
      const backgrounds = props.image?.audioBackground;
      if (mediaTypeId.value !== MediaType.mp3 || !backgrounds || !backgrounds.length) {
        return undefined;
      }
      return [...backgrounds];
    });

    const currentAudioBackgroundUrl = computed(() => {
      if (!audioBackground.value) {
        return undefined;
      }
      const currentBackground =
        audioBackground.value?.find((media) => media?.pageTimingToMillisecond && media.pageTimingToMillisecond > audioLocalTime.value * 1000) ??
        audioBackground.value?.[audioBackground.value?.length - 1];
      return currentBackground.image?.url;
    });

    const onAudioTimeUpdate = () => {
      audioLocalTime.value = audioAnnotation.value?.currentTime ?? 0;
    };

    // ---------------------------------------------------------------------------
    // Board image loading (spin)
    // ---------------------------------------------------------------------------
    const boardImageUrl = ref<string | undefined>();
    const isBoardImageFetching = ref(false);
    const boardImageLoadingTimeoutId = ref<any>();
    const boardImageLoadingThreshold = 300;

    const toggleImageFetchingSpin = (visible: boolean) => {
      if (boardImageLoadingTimeoutId.value) clearTimeout(boardImageLoadingTimeoutId.value);
      if (visible && !isTeacherUseOnly.value) {
        boardImageLoadingTimeoutId.value = setTimeout(() => {
          isBoardImageFetching.value = true;
        }, boardImageLoadingThreshold);
      } else {
        if (isBoardImageFetching.value) isBoardImageFetching.value = false;
      }
    };

    const resetBoardImageLoadingRefs = () => {
      resetReloadKey();
      if (isBoardImageFetching.value) isBoardImageFetching.value = false;
      if (boardImageLoadingTimeoutId.value) clearTimeout(boardImageLoadingTimeoutId.value);
    };

    watch(boardImageUrl, (val) => {
      resetBoardImageLoadingRefs();
      if (val) toggleImageFetchingSpin(true);
    });

    // Switch crop → normal image, cùng URL
    watch(isCropImage, (val) => {
      if (!val) toggleImageFetchingSpin(true);
    });

    watchEffect(() => {
      if (mediaTypeId.value === undefined) {
        boardImageUrl.value = props.image?.url;
      } else if (mediaTypeId.value === MediaType.mp3) {
        boardImageUrl.value = currentAudioBackgroundUrl.value;
      } else {
        boardImageUrl.value = undefined;
      }
    });

    // ---------------------------------------------------------------------------
    // Media canPlay & sinkId (device output)
    // ---------------------------------------------------------------------------
    const speakerDeviceId = computed(() => getters[vuexName(MODULES.APP.GETTERS.GET_SPEAKER_DEVICE_ID, false)]);

    const setSinkId = async () => {
      if (mediaTypeId.value === MediaType.mp4) {
        const videoEl = document.getElementById(ALTERNATE_VIDEO_ID) as HTMLVideoElement;
        if (typeof videoEl?.setSinkId === "function") {
          await videoEl?.setSinkId(speakerDeviceId.value);
        }
      } else if (mediaTypeId.value === MediaType.mp3) {
        const audioEl = document.getElementById(ALTERNATE_AUDIO_ID) as HTMLAudioElement;
        if (typeof audioEl?.setSinkId === "function") {
          await audioEl?.setSinkId(speakerDeviceId.value);
        }
      }
    };

    watch(speakerDeviceId, setSinkId);

    watch(
      () => props.image?.url,
      async () => {
        // Run before old videoRef/audioRef is removed
        if (videoAnnotation.value) {
          videoAnnotation.value.oncanplay = null;
        }
        if (audioAnnotation.value) {
          audioAnnotation.value.oncanplay = null;
        }
        toggleMediaCanPlay(false);
        await nextTick();
        // Run after new videoRef/audioRef is created
        if (videoAnnotation.value) {
          videoRef.value = videoAnnotation.value;
          videoAnnotation.value.onloadstart = setSinkId;
          videoAnnotation.value.oncanplay = () => {
            toggleMediaCanPlay(true);
          };
        }
        if (audioAnnotation.value) {
          audioRef.value = audioAnnotation.value;
          audioAnnotation.value.onloadstart = setSinkId;
          audioAnnotation.value.oncanplay = () => {
            toggleMediaCanPlay(true);
          };
        }
      },
    );

    // ---------------------------------------------------------------------------
    // Error handling
    // ---------------------------------------------------------------------------
    const onErrorLoadSlide = () => {
      canvas.getObjects().forEach((obj: any) => {
        if (obj.type === FbObjects.Group) {
          canvas.remove(obj);
        }
      });
      handleImageLoadError();
    };

    // ---------------------------------------------------------------------------
    // Lifecycle
    // ---------------------------------------------------------------------------
    onMounted(async () => {
      await initWhiteboard();
    });

    onBeforeUnmount(() => {
      if (videoAnnotation.value) {
        videoAnnotation.value.pause();
        videoAnnotation.value.src = "";
      }
      if (audioAnnotation.value) {
        audioAnnotation.value.pause();
        audioAnnotation.value.src = "";
      }
    });

    // ---------------------------------------------------------------------------
    // Return bindings
    // ---------------------------------------------------------------------------
    return {
      containerRef,
      containerWidth,
      pointerStyle,

      canvasRef,
      isWbVisible,
      student,
      oneToOneStudentId,

      mediaTypeId,
      isPointerMode,

      onLPSlideLoaded,
      videoAnnotation,
      audioAnnotation,

      isValidUrl,
      isCropImage,
      imgCropRef,
      imgRef,

      focusWordContent,

      onPdfRendered,
      currentExposureItemMedia,

      onAudioTimeUpdate,
      currentAudioBackgroundUrl,

      MediaType,
      WarningAudioTagText,
      WarningVideoTagText,

      pdfRef,
      isBoardImageFetching,
      onErrorLoadSlide,
      boardImageUrl,
      reloadKey,
      isTeacherDisconnected,

      DEFAULT_ALTERNATE_CONTENT_VOLUME_FOR_STUDENT,
      ALTERNATE_VIDEO_ID,
      ALTERNATE_AUDIO_ID,
    };
  },
});
